import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {handleErrorResponse} from "../utils/util"
import { USER_ROLE } from "../constants/index"
import { ContentService } from "../api/services.gen";
import { 
  ContentDetailRetrieveData, 
  ContentRelatedListData, 
  ContentNotificationSettingCreateData,
  ContentNotificationSettingUpdateData,
  ContentManageNewsListListData,
  ContentChartNewsRetrieveData,
} from "../api/types.gen"
import i18n from 'i18next';
import { resetAppState } from "../store/common";

export const fetchTopNews = createAsyncThunk(
  "fetchTopNews",
  async (_, thunkAPI: any) => {
    try {
      const res: any = await ContentService.contentManageTopNewsList();
      if (res.success) {
        return res.data;
      } else {
        if(res.message) return thunkAPI.rejectWithValue(res.message);
        else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
      }
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);

export const fetchNewsList = createAsyncThunk(
  "fetchNewsList",
  async (data: ContentManageNewsListListData, thunkAPI: any) => {
    try {
      const res: any = await ContentService.contentManageNewsListList(data);
      if (res.results.success) {
        return res;
      } else {
        if(res.message) return thunkAPI.rejectWithValue(res.message);
        else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
      }
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);

export const getNewsDetail = createAsyncThunk(
  "getNewsDetail",
  async (data: ContentDetailRetrieveData, thunkAPI: any) => {
    try {
      const res: any = await ContentService.contentDetailRetrieve(data);
      if (res.id) {
        return res;
      } else {
        if(res.message) return thunkAPI.rejectWithValue(res.message);
        else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
      }
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);

export const fetchRelated = createAsyncThunk(
  "fetchRelated",
  async (data: ContentRelatedListData, thunkAPI: any) => {
    try {
      const res: any = await ContentService.contentRelatedList(data);
      if (res.success) {
        return res.data;
      } else {
        if(res.message) return thunkAPI.rejectWithValue(res.message);
        else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
      }
    } catch (error: any) {
      if(error.status == 404){
        return thunkAPI.rejectWithValue("指定されたIDのニュースは存在しません。");
      }
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);

export const getNotiSetting = createAsyncThunk(
  "getNotiSetting",
  async (_, thunkAPI: any) => {
    try {
      const res: any = await ContentService.contentNotificationSettingRetrieve();
      if (res.success) {
        return res.data;
      } else {
        if(res.message) return thunkAPI.rejectWithValue(res.message);
        else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
      }
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);

export const createNotiSetting = createAsyncThunk(
  "createNotiSetting",
  async (data: ContentNotificationSettingCreateData, thunkAPI: any) => {
    try {
      const res: any = await ContentService.contentNotificationSettingCreate(data);
      if (res.success) {
        return res;
      } else {
        if(res.message) return thunkAPI.rejectWithValue(res.message);
        else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
      }
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);

export const editNotiSetting = createAsyncThunk(
  "editNotiSetting",
  async (data: ContentNotificationSettingUpdateData, thunkAPI: any) => {
    try {
      const res: any = await ContentService.contentNotificationSettingUpdate(data);
      if (res.success) {
        return res;
      } else {
        if(res.message) return thunkAPI.rejectWithValue(res.message);
        else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
      }
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);

export const getNotiList = createAsyncThunk(
  "getNotiList",
  async (_, thunkAPI: any) => {
    try {
      const res: any = await ContentService.contentNotificationListList();
      if (res.success) {
        return res.data;
      } else {
        if(res.message) return thunkAPI.rejectWithValue(res.message);
        else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
      }
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);

export const getNewsViewHistoryChart = createAsyncThunk(
  "getNewsViewHistoryChart",
  async (data: ContentChartNewsRetrieveData, thunkAPI: any) => {
    try {
      const res: any = await ContentService.contentChartNewsRetrieve(data);
      if (res.success) {
        return res.data;
      } else {
        if(res.message) return thunkAPI.rejectWithValue(res.message);
        else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
      }
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);

export const updateNewsStatus = createAsyncThunk(
  "updateNewsStatus",
  async (data: ContentCreateUpdateData, thunkAPI: any) => {
    try {
      const res: any = await ContentService.contentCreateUpdate(data);
      if (res.success) {
        return res;
      } else {
        if(res.message) return thunkAPI.rejectWithValue(res.message);
        else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
      }
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);

// Initial state of Redux
interface ContentState {
  loading: boolean;
  error: string | null;
  success: string | null;
  topNews: [];
  newsList: [];
  detailData: [];
  relatedData: [];
  notiSettingData: [];
  notiList: [];
  countItems: number;
  countPage: number;
  currentPage: number;
  pageSize: number;
  isValidChart: boolean;
  chartData: {};
  actionStatus: string;
  actionName: string;
  isReload: boolean;
}

const initialState: ContentState = {
  loading: false,
  error: null,
  success: null,
  topNews: [],
  newsList: [],
  detailData: [],
  relatedData: [],
  notiSettingData: [],
  notiList: [],
  countItems: 0,
  countPage: 1,
  currentPage: 1,
  pageSize: 10,
  isValidChart: false,
  chartData: {
    'pData': [],
    'uData': [],
    'xLabels': []
  },
  actionStatus: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  actionName: "",
  isReload: false,
};

export const contentManageSlice = createSlice({
  name: 'contentManage',
  initialState,
  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setSuccess: (state, action) => {
      state.success = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // reset state
      .addCase(resetAppState, () => initialState)

      // fetchTopNews
      .addCase(fetchTopNews.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(fetchTopNews.fulfilled, (state:any, action) => {
        state.loading = false;
        state.topNews = action.payload;
      })
      .addCase(fetchTopNews.rejected, (state:any, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // fetchNewsList
      .addCase(fetchNewsList.pending, (state) => {
        state.loading = true;
        state.success = null;
        state.error = null;
        state.actionName = "fetchNewsList";
        state.actionStatus = "pending";
      })
      .addCase(fetchNewsList.fulfilled, (state:any, action) => {
        state.loading = false;
        state.newsList = action.payload?.results?.data;
        state.countItems = action.payload?.count;
        state.countPage = Math.ceil(state?.countItems / state?.pageSize);
        state.actionName = "fetchNewsList";
        state.actionStatus = "fulfilled";
        if (action.payload.success) {
          state.error = null;
          state.success = action.payload.message;
        } else {
          state.success = null;
          state.error = action.payload.message;
        }
      })
      .addCase(fetchNewsList.rejected, (state:any, action) => {
        state.loading = false;
        state.error = action.payload;
        state.actionName = "fetchNewsList";
        state.actionStatus = "rejected";
      })

      // getDetailNews
      .addCase(getNewsDetail.pending, (state) => {
        state.loading = true;
        state.success = null;
        state.error = null;
        state.actionStatus = "pending";
        state.actionName = "getDetailNews";
      })
      .addCase(getNewsDetail.fulfilled, (state:any, action) => {
        state.loading = false;
        state.detailData = action.payload;
        state.actionStatus = "fulfilled";
      })
      .addCase(getNewsDetail.rejected, (state:any, action) => {
        state.loading = false;
        state.success = null;
        state.error = action.payload;
        state.actionStatus = "rejected";
      })

      // fetchRelated
      .addCase(fetchRelated.pending, (state) => {
        state.loading = true;
        state.success = null;
        state.error = null;
      })
      .addCase(fetchRelated.fulfilled, (state:any, action) => {
        state.loading = false;
        state.relatedData = action.payload;
      })
      .addCase(fetchRelated.rejected, (state:any, action) => {
        state.loading = false;
        state.success = null;
        state.error = action.payload;
      })

      // getNotiSetting
      .addCase(getNotiSetting.pending, (state) => {
        state.loading = true;
        state.success = null;
        state.error = null;
      })
      .addCase(getNotiSetting.fulfilled, (state:any, action) => {
        state.loading = false;
        state.notiSettingData = action.payload;
      })
      .addCase(getNotiSetting.rejected, (state:any, action) => {
        state.loading = false;
        state.success = null;
        state.error = action.payload;
      })

      // createNotiSetting
      .addCase(createNotiSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createNotiSetting.fulfilled, (state:any, action) => {
        state.loading = false;
        state.notiSettingData = action.payload.data;
        state.success = action.payload.message;
      })
      .addCase(createNotiSetting.rejected, (state:any, action) => {
        state.loading = false;
        state.success = null;
        state.error = action.payload;
      })

      // editNotiSetting
      .addCase(editNotiSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(editNotiSetting.fulfilled, (state:any, action) => {
        state.loading = false;
        state.notiSettingData = action.payload.data;
        state.success = action.payload.message;
      })
      .addCase(editNotiSetting.rejected, (state:any, action) => {
        state.loading = false;
        state.success = null;
        state.error = action.payload;
      })

      // getNotiList
      .addCase(getNotiList.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(getNotiList.fulfilled, (state:any, action) => {
        state.loading = false;
        state.notiList = action.payload;
      })
      .addCase(getNotiList.rejected, (state:any, action) => {
        state.loading = false;
        state.success = null;
        state.error = action.payload;
      })

      // getNewsViewHistoryChart
      .addCase(getNewsViewHistoryChart.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(getNewsViewHistoryChart.fulfilled, (state:any, action) => {
        state.loading = false;
        state.chartData = action.payload;
        state.isValidChart = action.payload.is_valid;
      })
      .addCase(getNewsViewHistoryChart.rejected, (state:any, action) => {
        state.loading = false;
        state.success = null;
        state.error = action.payload;
      })

      // updateNewsStatus
      .addCase(updateNewsStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
        state.actionName = "updateNewsStatus"
        state.actionStatus = "pending";
      })
      .addCase(updateNewsStatus.fulfilled, (state:any, action) => {
        state.actionName = "updateNewsStatus"
        state.actionStatus = "fulfilled";
        state.loading = false;
        if(action.payload.success) {
          state.error = null;
          state.success = action.payload.message;
        } else {
          state.success = null;
          state.error = action.payload.message;
        }
        state.isReload = !state.isReload;
      })
      .addCase(updateNewsStatus.rejected, (state:any, action) => {
        state.actionName = "updateNewsStatus"
        state.actionStatus = "rejected";
        state.loading = false;
        state.success = null;
        state.error = action.payload;
      })
  },
});
export const {
  setCurrentPage, setError, setSuccess
} = contentManageSlice.actions;
export default contentManageSlice.reducer;